import React from 'react';
import PropTypes from 'prop-types';
import Loader from 'relements/build/esm/decorators/loader';
import Table from 'relements/build/esm/ui/table';
import Search from 'relements/build/esm/input/search';
import Button from 'relements/build/esm/ui/button';

// Decorators
import connection from '@src/connection';

// Actions
import { openComponentModal } from '@src/actions/BotActions';
import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';

// Constants
import { ENTITY_CATEGORY, ENTITY_TYPES } from '@src/constants/Entities';
import { GOOGLE_ANALYTICS } from '@src/const';

// Components
import EntityEditor from '@src/containers/Entities/EntityEditor';

// Local
import WithTooltip from 'relements/build/esm/overlays/withTooltip';
import { LOCAL_ENTITY_TOOLTIP } from '@src/constants/Tooltips';
import EntityCategoryLabel from './EntityCategoryLabel';
import EntityTypeLabel from './EntityTypeLabel';
import EntityNameLabel from './EntityNameLabel';
import EntityLanguagesLabel from './EntityLanguagesLabel';
import EntityCopyModal from './EntityCopyModal';
import EmptyState from './EmptyState';
import ZeroResults from './ZeroResults';

import * as maps from './maps';
import styles from './EntitySearch.scss';
import EntitySecurityLabel from './EntitySecurityLabel';

const COLUMNS = [
  {
    title: 'Entity Name and Description',
    width: '25%',
    className: styles.tableHeaderItemText,
  },
  {
    title: 'Entity Type',
    width: '12%',
    className: styles.tableHeaderItemText,
  },
  {
    title: 'Category',
    width: '10%',
    className: styles.tableHeaderItemText,
  },
  {
    title: 'User data privacy',
    width: '14%',
    className: styles.tableHeaderItemText,
  },
  {
    title: 'Language',
    width: '18%',
    className: styles.tableHeaderItemText,
  },
  {
    title: '',
    width: '12%',
    className: styles.tableHeaderItemText,
  },
  {
    title: '',
    width: '10%',
    className: styles.tableHeaderItemText,
  },
];

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
class EntitySearch extends React.Component {
  constructor() {
    super();

    this.state = {
      searchResults: [],
      searchStarted: false,
      entityToCopy: {},
      copyModalActive: false,
    };
  }

  componentDidMount() {
    if (this.props.initSearch) {
      this.onSearch('');
    }
  }

  render() {
    const tableData = this.generateTableData(this.state.searchResults);
    const { hideCreateNewEntity } = this.props;
    return (
      <div className={styles.entitySearchContainer}>
        <div className={styles.searchRow}>
          <Search
            className={styles.searchBar}
            onSubmit={(e) => {
              if (e.target.value) this.onSearch(e.target.value);
            }}
            placeholder="search an entity, word or variant"
            autoFocus
            hint="↩ Press enter to search"
          />
          {!hideCreateNewEntity && (
            <WithTooltip
              tooltip={LOCAL_ENTITY_TOOLTIP.text}
              prefixClassName={styles.entitySearchTooltip}
            >
              <Button
                onClick={this.createNewEntity}
                type={Button.TYPES.SECONDARY}
              >
                + Create Local Entity
              </Button>
            </WithTooltip>
          )}
        </div>
        <div className={styles.searchResults}>
          {!this.state.searchStarted && <EmptyState />}
          {this.state.searchResults.length > 0 && (
            <>
              <div className={styles.searchResultCount}>
                <div className={styles.label}>
                  Showing {this.state.searchResults.length} Search Results
                </div>
                <div className={styles.tip}>
                  <a
                    target="_blank"
                    href="https://docs.haptik.ai/entities/what-are-entities"
                    rel="noreferrer"
                  >
                    Learn more about public and local entities
                  </a>
                </div>
              </div>
              <Table
                simple
                rows={tableData}
                columns={COLUMNS}
                prefixClassName={styles.entitySearchTable}
              />
            </>
          )}
          {!this.props.loaderActive &&
            this.state.searchResults.length === 0 &&
            this.state.searchStarted && (
              <div style={{ padding: '10px' }}>
                <div className={styles.label}>No Results Found! </div>
                <ZeroResults
                  onCreateNewEntity={this.createNewEntity}
                  hideCreateNewEntity={this.props.hideCreateNewEntity}
                />
              </div>
            )}
        </div>
        <EntityCopyModal
          onCopy={this.copyEntity}
          onClose={this.onClose}
          active={this.state.copyModalActive}
          entityToCopy={this.state.entityToCopy}
        />
      </div>
    );
  }

  onClose = () => {
    this.setState({ copyModalActive: false });
  };

  onSearch = (searchTerm) => {
    this.setState({
      searchResults: [],
      searchStarted: true,
    });
    this.props.activateLoader();
    maps.searchForEntities({
      ...this.props.searchFilters,
      term: searchTerm,
      callback: this.onResultsReceived,
      bot_name: this.props.domainName,
    });
  };

  onResultsReceived = (searchResults) => {
    const cleanedResults = this.cleanResults(searchResults);
    const orderedSearchResults = this.sortResults(cleanedResults);
    this.setState({
      searchResults: orderedSearchResults,
    });
    if (this.props.loaderActive) this.props.deactivateLoader();
  };

  cleanResults = (results) => {
    return results.map((result) => {
      const updatedResult = result;
      updatedResult.entity_category = maps.convertEntityCategory(
        result.entity_category,
        result.domain_name,
        this.props.domainName
      );
      return updatedResult;
    });
  };

  sortResults = (results) => {
    const entityCategoryPreference = [
      ENTITY_CATEGORY.DOMAIN,
      ENTITY_CATEGORY.SYSTEM,
      ENTITY_CATEGORY.DEPRECATED,
    ];

    return results.sort((resultA, resultB) => {
      const preferenceA = entityCategoryPreference.indexOf(
        resultA.entity_category
      );
      const preferenceB = entityCategoryPreference.indexOf(
        resultB.entity_category
      );
      return preferenceA > preferenceB ? 1 : -1;
    });
  };

  generateTableData = (searchResults) => {
    return searchResults.map((result) => {
      const row = {
        id: result.name,
        columns: [],
      };

      row.columns.push(
        this.generateColumn(
          <EntityNameLabel
            name={result.name}
            description={result.description}
          />
        )
      );
      row.columns.push(
        this.generateColumn(<EntityTypeLabel entityType={result.entity_type} />)
      );
      row.columns.push(
        this.generateColumn(
          <EntityCategoryLabel category={result.entity_category} />
        )
      );
      row.columns.push(
        this.generateColumn(
          <EntitySecurityLabel security={result.personal_data} />
        )
      );
      row.columns.push(
        this.generateColumn(
          <EntityLanguagesLabel languages={result.supported_languages} />
        )
      );

      let addActionable = null;

      if (
        this.props.entityForOutputNode &&
        this.props.entityOnOutputNode === result.name
      ) {
        // Already Present
        addActionable = (
          <Button className={styles.fullWidthButton} disabled>
            Already Present
          </Button>
        );
      } else if (
        !this.props.entityForOutputNode &&
        this.props.entitiesOnNode.includes(result.name)
      ) {
        // Already Present
        addActionable = (
          <Button className={styles.fullWidthButton} disabled>
            Already Present
          </Button>
        );
      } else if (result.entity_category === ENTITY_CATEGORY.DEPRECATED) {
        if (ENTITY_TYPES.includes(result.entity_type)) {
          // Copy to current Domain
          addActionable = this.props.onCopyEntity ? (
            <Button
              className={styles.fullWidthButton}
              onClick={() => this.openCopyModal(result)}
              type={Button.TYPES.SECONDARY}
            >
              Copy Entity
            </Button>
          ) : null;
        }
      } else {
        // Add to Bot Says
        addActionable = this.props.onAddEntityToNode ? (
          <Button
            className={styles.fullWidthButton}
            onClick={() => this.addToNode(result)}
            type={Button.TYPES.SECONDARY}
          >
            Add to Bot Responses
          </Button>
        ) : null;
      }
      row.columns.push(this.generateColumn(addActionable));

      let viewActionable = null;
      if (
        result.entity_category === ENTITY_CATEGORY.DOMAIN &&
        result.domain_name === this.props.domainName
      ) {
        viewActionable = (
          <Button
            className={styles.fullWidthButton}
            onClick={() => this.editEntity(result)}
            type={Button.TYPES.SECONDARY}
          >
            View/Edit
          </Button>
        );
      } else {
        viewActionable = (
          <Button
            className={styles.fullWidthButton}
            onClick={() => this.viewEntity(result)}
            type={Button.TYPES.SECONDARY}
          >
            View
          </Button>
        );
      }
      row.columns.push(this.generateColumn(viewActionable));

      return row;
    });
  };

  generateColumn = (content) => ({
    content,
  });

  openCopyModal = (entityData) => {
    this.setState({ entityToCopy: entityData, copyModalActive: true });
  };

  copyEntity = async ({ srcName, dstName, languageList }) => {
    this.props.activateLoader();
    this.props.onCopyEntity(srcName, dstName, languageList, () => {
      this.props.deactivateLoader();
    });
  };

  addToNode = (entityData) => {
    // add entity to bot says
    this.props.onAddEntityToNode(
      entityData.name,
      entityData.id,
      entityData.quickResponse || [],
      entityData.responses || []
    );
  };

  editEntity = (entityData) => {
    // openComponentModal in edit state to save entity data
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.ENTITY,
      GOOGLE_ANALYTICS.EVENTS.ENTITY_EDIT,
      `entity-${entityData.name}`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
    openComponentModal(
      <EntityEditor
        entityName={entityData.name}
        entityId={entityData.id}
        onAddEntityToNode={this.props.onAddEntityToNode}
        onCopyEntity={this.props.onCopyEntity}
        isSuperman={this.props.isSuperman}
        supermanSupportedLanguages={this.props.supermanSupportedLanguages}
        edit
      />,
      entityData.name,
      null,
      'CROSS'
    );
  };

  createNewEntity = () => {
    // openComponentModal in read only mode to save entity data
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.ENTITY,
      GOOGLE_ANALYTICS.EVENTS.ENTITY_EDIT,
      'entity-create-new',
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
    openComponentModal(
      <EntityEditor
        new
        edit
        onAddEntityToNode={this.props.onAddEntityToNode}
        onCopyEntity={this.props.onCopyEntity}
        isSuperman={this.props.isSuperman}
        supermanSupportedLanguages={this.props.supermanSupportedLanguages}
      />,
      'Create New Entity',
      null,
      'CROSS'
    );
  };

  viewEntity = (entityData) => {
    // openComponentModal in read only mode to save entity data
    sendGoogleAnalyticsEvents(
      GOOGLE_ANALYTICS.CATEGORY.ENTITY,
      GOOGLE_ANALYTICS.EVENTS.ENTITY_VIEW,
      `entity-${entityData.name}`,
      GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
    );
    openComponentModal(
      <EntityEditor
        entityName={entityData.name}
        entityId={entityData.id}
        onCopyEntity={this.props.onCopyEntity}
        onAddEntityToNode={this.props.onAddEntityToNode}
        isSuperman={this.props.isSuperman}
        supermanSupportedLanguages={this.props.supermanSupportedLanguages}
      />,
      entityData.name,
      null,
      'CROSS'
    );
  };
}

EntitySearch.propTypes = {
  activateLoader: PropTypes.func.isRequired,
  deactivateLoader: PropTypes.func.isRequired,
  loaderActive: PropTypes.bool.isRequired,
  isSuperman: PropTypes.bool.isRequired,
  entitiesOnNode: PropTypes.arrayOf(PropTypes.string).isRequired,
  onAddEntityToNode: PropTypes.func,
  hideCreateNewEntity: PropTypes.bool,
  onCopyEntity: PropTypes.func.isRequired,
  domainName: PropTypes.string.isRequired,
  entityForOutputNode: PropTypes.bool,
  entityOnOutputNode: PropTypes.string,
  supermanSupportedLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      path: PropTypes.string,
    })
  ),
};

EntitySearch.defaultProps = {
  onAddEntityToNode: null,
  hideCreateNewEntity: false,
  entityForOutputNode: false,
  entityOnOutputNode: '',
  supermanSupportedLanguages: [],
};

export default EntitySearch;
